import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ChefHat, X } from "lucide-react";

const CATEGORIES = ["Starter", "Main course", "Bread", "Side", "Dessert", "Beverage"];
const emptyIng = { name: "", quantity: "", unit: "g", cost: "" };
const emptyForm = {
  name: "",
  category: "Main course",
  servings: 1,
  ingredients: [{ ...emptyIng }],
  cooking_notes: "",
  sale_price: "",
};

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function RecipesPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "head_chef";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { category: filter } : {};
      const { data } = await api.get("/recipes", { params });
      setRows(data);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const totalCostPreview = useMemo(
    () => form.ingredients.reduce((s, i) => s + (parseFloat(i.cost) || 0), 0),
    [form.ingredients]
  );
  const perServingPreview = form.servings > 0 ? (totalCostPreview / form.servings) : totalCostPreview;
  const marginPreview =
    form.sale_price && perServingPreview > 0
      ? (((parseFloat(form.sale_price) - perServingPreview) / parseFloat(form.sale_price)) * 100).toFixed(1)
      : null;

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, ingredients: [{ ...emptyIng }] });
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name,
      category: r.category,
      servings: r.servings,
      ingredients: r.ingredients?.length
        ? r.ingredients.map((i) => ({ ...i, quantity: String(i.quantity), cost: String(i.cost) }))
        : [{ ...emptyIng }],
      cooking_notes: r.cooking_notes || "",
      sale_price: r.sale_price != null ? String(r.sale_price) : "",
    });
    setOpen(true);
  };

  const setIng = (idx, key, value) => {
    setForm((f) => {
      const next = [...f.ingredients];
      next[idx] = { ...next[idx], [key]: value };
      return { ...f, ingredients: next };
    });
  };
  const addIng = () => setForm((f) => ({ ...f, ingredients: [...f.ingredients, { ...emptyIng }] }));
  const rmIng = (i) => setForm((f) => ({ ...f, ingredients: f.ingredients.filter((_, idx) => idx !== i) }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        category: form.category,
        servings: parseInt(form.servings, 10) || 1,
        cooking_notes: form.cooking_notes,
        sale_price: form.sale_price === "" ? null : parseFloat(form.sale_price),
        ingredients: form.ingredients
          .filter((i) => i.name && i.name.trim())
          .map((i) => ({
            name: i.name.trim(),
            quantity: parseFloat(i.quantity) || 0,
            unit: i.unit || "g",
            cost: parseFloat(i.cost) || 0,
          })),
      };
      if (editing) {
        await api.put(`/recipes/${editing.id}`, body);
        toast.success("Recipe updated");
      } else {
        await api.post("/recipes", body);
        toast.success("Recipe saved");
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (r) => {
    if (!window.confirm(`Delete recipe "${r.name}"?`)) return;
    try {
      await api.delete(`/recipes/${r.id}`);
      toast.success("Recipe removed");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div data-testid="recipes-page">
      <div className="fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Kitchen</p>
          <h1 className="font-serif text-3xl sm:text-5xl tracking-tight mt-2 flex items-center gap-3">
            Recipes <ChefHat className="w-8 h-8 text-primary" aria-hidden="true" />
          </h1>
          <p className="mt-3 text-muted-foreground">
            Every dish with its ingredients, portion costs, and expected margin.
          </p>
        </div>
        {canEdit && (
          <Button onClick={openNew} data-testid="add-recipe-btn" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            <Plus className="w-4 h-4 mr-2" aria-hidden="true" /> New recipe
          </Button>
        )}
      </div>

      <div className="mt-8 flex items-center gap-3">
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Category</Label>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-52" data-testid="recipe-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full bg-white border border-border p-10 text-center text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="col-span-full bg-white border border-border p-14 text-center">
            <p className="font-serif text-2xl">No recipes yet.</p>
            <p className="text-muted-foreground mt-2">Tap "New recipe" to file your first dish.</p>
          </div>
        ) : (
          rows.map((r) => (
            <div key={r.id} className="bg-white border border-border p-6 fade-up cursor-pointer hover:border-primary transition-colors" onClick={() => setViewing(r)} data-testid={`recipe-card-${r.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">{r.category}</div>
                  <div className="font-serif text-2xl mt-1">{r.name}</div>
                </div>
                {canEdit && (
                  <div className="flex" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-recipe-${r.id}`}><Pencil className="w-4 h-4" aria-hidden="true" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => doDelete(r)} data-testid={`delete-recipe-${r.id}`}><Trash2 className="w-4 h-4 text-destructive" aria-hidden="true" /></Button>
                  </div>
                )}
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                <div>
                  <div className="uppercase tracking-widest text-muted-foreground">Cost</div>
                  <div className="font-serif text-lg text-foreground">{fmt(r.total_cost)}</div>
                </div>
                <div>
                  <div className="uppercase tracking-widest text-muted-foreground">Per serving</div>
                  <div className="font-serif text-lg text-primary">{fmt(r.cost_per_serving)}</div>
                </div>
                <div>
                  <div className="uppercase tracking-widest text-muted-foreground">Sale</div>
                  <div className="font-serif text-lg">{r.sale_price ? fmt(r.sale_price) : "—"}</div>
                </div>
              </div>
              <div className="mt-3 text-xs text-muted-foreground">
                {r.servings} serving{r.servings > 1 ? "s" : ""} · {(r.ingredients || []).length} ingredients
              </div>
            </div>
          ))
        )}
      </div>

      {/* Edit / add dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="recipe-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl tracking-tight">{editing ? "Edit recipe" : "New recipe"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="rname">Dish name</Label>
                <Input id="rname" data-testid="recipe-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Butter Chicken" />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="recipe-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="srv">Servings</Label>
                <Input id="srv" data-testid="recipe-servings" type="number" min="1" required value={form.servings} onChange={(e) => setForm({ ...form, servings: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="sp">Sale price (₹)</Label>
                <Input id="sp" data-testid="recipe-sale" type="number" min="0" step="0.01" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} placeholder="e.g. 450" />
              </div>
            </div>

            <div>
              <div className="flex items-baseline justify-between mb-2">
                <Label>Ingredients</Label>
                <Button type="button" size="sm" variant="ghost" onClick={addIng} data-testid="add-ingredient-btn">
                  <Plus className="w-3 h-3 mr-1" aria-hidden="true" /> Add
                </Button>
              </div>
              <div className="space-y-2" data-testid="ingredients-list">
                {form.ingredients.map((i, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-5" placeholder="Ingredient" value={i.name} onChange={(e) => setIng(idx, "name", e.target.value)} data-testid={`ing-name-${idx}`} />
                    <Input className="col-span-2" placeholder="Qty" type="number" step="0.01" min="0" value={i.quantity} onChange={(e) => setIng(idx, "quantity", e.target.value)} data-testid={`ing-qty-${idx}`} />
                    <Input className="col-span-2" placeholder="Unit" value={i.unit} onChange={(e) => setIng(idx, "unit", e.target.value)} data-testid={`ing-unit-${idx}`} />
                    <Input className="col-span-2" placeholder="Cost ₹" type="number" step="0.01" min="0" value={i.cost} onChange={(e) => setIng(idx, "cost", e.target.value)} data-testid={`ing-cost-${idx}`} />
                    <Button type="button" size="sm" variant="ghost" onClick={() => rmIng(idx)} className="col-span-1 text-destructive"><X className="w-4 h-4" aria-hidden="true" /></Button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Cooking notes</Label>
              <Textarea id="notes" data-testid="recipe-notes" rows={3} value={form.cooking_notes} onChange={(e) => setForm({ ...form, cooking_notes: e.target.value })} placeholder="Marinate chicken 4 hrs, then charcoal grill…" />
            </div>

            <div className="grid grid-cols-3 gap-3 text-sm bg-secondary p-4 border border-border">
              <div><div className="uppercase text-[10px] tracking-widest text-muted-foreground">Total cost</div><div className="font-serif text-xl">{fmt(totalCostPreview)}</div></div>
              <div><div className="uppercase text-[10px] tracking-widest text-muted-foreground">Per serving</div><div className="font-serif text-xl text-primary">{fmt(perServingPreview)}</div></div>
              <div><div className="uppercase text-[10px] tracking-widest text-muted-foreground">Margin</div><div className="font-serif text-xl">{marginPreview ? `${marginPreview}%` : "—"}</div></div>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="save-recipe-btn" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {saving ? "Saving…" : editing ? "Save changes" : "File recipe"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent data-testid="recipe-view-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl tracking-tight">{viewing?.name}</DialogTitle>
          </DialogHeader>
          {viewing && (
            <>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">{viewing.category} · {viewing.servings} serving{viewing.servings > 1 ? "s" : ""}</div>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div className="bg-secondary p-3 border border-border"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total</div><div className="font-serif text-xl">{fmt(viewing.total_cost)}</div></div>
                <div className="bg-primary text-primary-foreground p-3"><div className="text-[10px] uppercase tracking-widest opacity-70">Per serving</div><div className="font-serif text-xl">{fmt(viewing.cost_per_serving)}</div></div>
                <div className="bg-secondary p-3 border border-border"><div className="text-[10px] uppercase tracking-widest text-muted-foreground">Sale</div><div className="font-serif text-xl">{viewing.sale_price ? fmt(viewing.sale_price) : "—"}</div></div>
              </div>
              <h3 className="mt-6 font-serif text-lg">Ingredients</h3>
              <table className="w-full mt-2 text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                    <th className="py-2">Item</th><th className="py-2 text-right">Qty</th><th className="py-2 text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {(viewing.ingredients || []).map((i, idx) => (
                    <tr key={idx} className="border-t border-border">
                      <td className="py-2">{i.name}</td>
                      <td className="py-2 text-right text-muted-foreground">{i.quantity} {i.unit}</td>
                      <td className="py-2 text-right font-serif">{fmt(i.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {viewing.cooking_notes && (
                <>
                  <h3 className="mt-6 font-serif text-lg">Cooking notes</h3>
                  <p className="mt-2 text-sm whitespace-pre-wrap text-muted-foreground">{viewing.cooking_notes}</p>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
